import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ReserveHeader } from "@/components/ReserveHeader";
import { portfolioSignedUrl } from "@/lib/portfolio";
import { formatBarExperience } from "@/lib/format-experience";

export const Route = createFileRoute("/_authenticated/bartenders/$id")({
  head: () => ({ meta: [{ title: "Bartender Portfolio — The Hospitality Reserve" }] }),
  component: BartenderPortfolioPage,
});

type Profile = {
  id: string; full_name: string | null; headline: string | null; years_experience: number | null;
  specialties: string[] | null; avatar_path: string | null; about_me: string | null;
  is_verified: boolean; has_dbs: boolean; total_months_experience: number | null; skills: string[] | null;
};
type Media = { id: string; kind: "photo" | "flair_video"; storage_path: string; caption: string | null };
type Cert = { id: string; name: string; issuer: string | null; issued_on: string | null; credential_url: string | null };
type PVenue = { id: string; venue_name: string; role: string | null; city: string | null; started_on: string | null; ended_on: string | null; description: string | null; photo_path: string | null };
type Review = { id: string; rating: number; comment: string | null; created_at: string; venue_id?: string; venue_name?: string | null };

function StarBar({ label, count, total }: { label: number; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-3 text-muted-foreground">{label}</span>
      <span className="text-accent">★</span>
      <div className="flex-1 h-2 bg-muted overflow-hidden">
        <div className="h-full bg-accent transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right text-muted-foreground text-xs">{count}</span>
    </div>
  );
}

function RatingBadge({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={s <= rating ? "text-accent" : "text-muted-foreground/30"}>★</span>
      ))}
    </div>
  );
}

function BartenderPortfolioPage() {
  const { id } = useParams({ from: "/_authenticated/bartenders/$id" });
  const [profile, setProfile] = useState<Profile | null>(null);
  const [media, setMedia] = useState<Media[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [certs, setCerts] = useState<Cert[]>([]);
  const [pvenues, setPVenues] = useState<PVenue[]>([]);
  const [venueUrls, setVenueUrls] = useState<Record<string, string>>({});
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [{ data: p }, { data: m }, { data: c }, { data: v }, { data: r }] = await Promise.all([
        supabase.from("profiles").select("id,full_name,headline,years_experience,specialties,avatar_path,about_me,is_verified,has_dbs,total_months_experience,skills").eq("id", id).maybeSingle(),
        (supabase.from as any)("portfolio_media").select("*").eq("bartender_id", id).order("sort_order"),
        (supabase.from as any)("portfolio_certifications").select("*").eq("bartender_id", id).order("sort_order"),
        (supabase.from as any)("portfolio_venues").select("*").eq("bartender_id", id).order("sort_order"),
        supabase
          .from("reviews")
          .select("id,rating,comment,created_at, venue_id")
          .eq("bartender_id", id)
          .order("created_at", { ascending: false })
          .limit(20),
      ]);
      setProfile(p as Profile | null);
      setMedia((m ?? []) as Media[]);
      setCerts((c ?? []) as Cert[]);
      setPVenues((v ?? []) as PVenue[]);
      setAvatarUrl(await portfolioSignedUrl((p as Profile | null)?.avatar_path));
      const mu: Record<string, string> = {};
      await Promise.all((m ?? []).map(async (row: Media) => { const url = await portfolioSignedUrl(row.storage_path); if (url) mu[row.id] = url; }));
      setMediaUrls(mu);
      const vu: Record<string, string> = {};
      await Promise.all((v ?? []).map(async (row: PVenue) => { const url = await portfolioSignedUrl(row.photo_path); if (url) vu[row.id] = url; }));
      setVenueUrls(vu);

      // Enrich reviews with venue names
      const rawReviews = (r ?? []) as any[];
      const venueIds = [...new Set(rawReviews.map((rev) => rev.venue_id).filter(Boolean))] as string[];
      let venueNames: Record<string, string> = {};
      if (venueIds.length > 0) {
        const { data: vData } = await supabase.from("profiles").select("id,full_name").in("id", venueIds);
        (vData ?? []).forEach((vd: any) => { venueNames[vd.id] = vd.full_name ?? "Venue"; });
      }
      setReviews(rawReviews.map((rev) => ({ ...rev, venue_name: venueNames[rev.venue_id ?? ""] ?? "Venue" })));

      setLoading(false);
    })();
  }, [id]);

  const photos = media.filter((m) => m.kind === "photo");
  const videos = media.filter((m) => m.kind === "flair_video");
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  if (loading) return (
    <div className="min-h-screen bg-background"><ReserveHeader /><main className="mx-auto max-w-5xl px-6 py-10"><p className="text-sm text-muted-foreground">Loading…</p></main></div>
  );

  if (!profile) return (
    <div className="min-h-screen bg-background"><ReserveHeader /><main className="mx-auto max-w-5xl px-6 py-10"><p className="text-sm text-muted-foreground">Bartender not found.</p><Link to="/venue" className="mt-4 inline-block text-accent hover:underline">← Back</Link></main></div>
  );

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-5xl px-6 py-10 space-y-10">
        <div className="flex justify-end">
          <Link to="/venue" className="border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground hover:border-foreground">← Back</Link>
        </div>

        {/* Hero */}
        <section className="flex flex-col md:flex-row gap-8 items-start">
          <div className="w-32 h-32 rounded-full bg-muted overflow-hidden border border-border flex-shrink-0">
            {avatarUrl ? <img src={avatarUrl} alt={profile.full_name ?? ""} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-3xl text-muted-foreground">{(profile.full_name ?? "?").slice(0, 1)}</div>}
          </div>
          <div className="flex-1">
            <p className="tracking-wide-luxury text-accent">Bartender</p>
            <h1 className="mt-2 font-display text-4xl">{profile.full_name ?? "Bartender"}</h1>
            {profile.headline && <p className="mt-2 text-lg text-muted-foreground">{profile.headline}</p>}
            <span className="gold-divider mt-4 block" />
            <div className="mt-4 flex flex-wrap gap-3 text-xs tracking-wide-luxury">
              {profile.is_verified && <span className="border border-accent text-accent px-3 py-1">ID verified</span>}
              {profile.has_dbs && <span className="border border-accent text-accent px-3 py-1">DBS checked</span>}
              {typeof profile.total_months_experience === "number" && profile.total_months_experience > 0 ? (
                <span className="border border-border px-3 py-1">{formatBarExperience(profile.total_months_experience)} behind the bar</span>
              ) : profile.years_experience != null ? (
                <span className="border border-border px-3 py-1">{profile.years_experience}+ yrs experience</span>
              ) : null}
              {avgRating && <span className="border border-border px-3 py-1">★ {avgRating} · {reviews.length} reviews</span>}
            </div>
            {(profile.specialties ?? []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {(profile.specialties ?? []).map((s, i) => <span key={i} className="bg-muted text-xs px-3 py-1">{s}</span>)}
              </div>
            )}
            {profile.about_me && <p className="mt-6 text-sm whitespace-pre-line max-w-2xl">{profile.about_me}</p>}
          </div>
        </section>

        {/* Photos */}
        {photos.length > 0 && (
          <section>
            <h2 className="font-display text-2xl mb-4">Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {photos.map((m) => (
                <figure key={m.id} className="border border-border bg-card">
                  {mediaUrls[m.id] && <img src={mediaUrls[m.id]} alt={m.caption ?? ""} className="w-full aspect-square object-cover" />}
                  {m.caption && <figcaption className="p-2 text-xs text-muted-foreground">{m.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Videos */}
        {videos.length > 0 && (
          <section>
            <h2 className="font-display text-2xl mb-4">Flair videos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {videos.map((m) => (
                <figure key={m.id} className="border border-border bg-card">
                  {mediaUrls[m.id] && <video src={mediaUrls[m.id]} controls className="w-full aspect-video bg-black" />}
                  {m.caption && <figcaption className="p-2 text-xs text-muted-foreground">{m.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {certs.length > 0 && (
          <section>
            <h2 className="font-display text-2xl mb-4">Certifications</h2>
            <div className="grid md:grid-cols-2 gap-3">
              {certs.map((c) => (
                <div key={c.id} className="border border-border bg-card p-4">
                  <p className="font-medium">{c.name}</p>
                  {c.issuer && <p className="text-xs text-muted-foreground mt-1">{c.issuer}</p>}
                  {c.issued_on && <p className="text-xs text-muted-foreground">Issued {new Date(c.issued_on).toLocaleDateString()}</p>}
                  {c.credential_url && <a href={c.credential_url} target="_blank" rel="noopener noreferrer" className="text-xs text-accent hover:underline mt-2 inline-block">View credential →</a>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Previous venues */}
        {pvenues.length > 0 && (
          <section>
            <h2 className="font-display text-2xl mb-4">Previous venues</h2>
            <div className="space-y-4">
              {pvenues.map((v) => (
                <div key={v.id} className="border border-border bg-card p-4 flex gap-4">
                  {venueUrls[v.id] && <img src={venueUrls[v.id]} alt={v.venue_name} className="w-28 h-28 object-cover border border-border flex-shrink-0" />}
                  <div className="flex-1">
                    <p className="font-display text-lg">{v.venue_name}</p>
                    <p className="text-xs tracking-wide-luxury text-muted-foreground mt-1">
                      {[v.role, v.city].filter(Boolean).join(" · ")}
                      {(v.started_on || v.ended_on) && <span> · {v.started_on ? new Date(v.started_on).getFullYear() : "?"}–{v.ended_on ? new Date(v.ended_on).getFullYear() : "present"}</span>}
                    </p>
                    {v.description && <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{v.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Reviews & Ratings */}
        {reviews.length > 0 && (
          <section className="border border-border bg-card p-6 md:p-8">
            <h2 className="font-display text-2xl mb-6">Reviews & Ratings</h2>
            <div className="flex flex-col md:flex-row gap-8">
              {/* Summary */}
              <div className="flex-shrink-0 md:w-56 space-y-4">
                <div>
                  <p className="font-display text-5xl">{avgRating}</p>
                  <div className="mt-1"><RatingBadge rating={Math.round(Number(avgRating))} /></div>
                  <p className="mt-1 text-sm text-muted-foreground">{reviews.length} review{reviews.length === 1 ? "" : "s"}</p>
                </div>
                <div className="space-y-1">
                  {[5, 4, 3, 2, 1].map((s) => (
                    <StarBar key={s} label={s} count={reviews.filter((r) => r.rating === s).length} total={reviews.length} />
                  ))}
                </div>
              </div>
              {/* Review list */}
              <div className="flex-1 space-y-4">
                {reviews.slice(0, 6).map((r) => (
                  <div key={r.id} className="border-b border-border last:border-0 pb-4 last:pb-0">
                    <div className="flex items-center justify-between">
                      <RatingBadge rating={r.rating} />
                      <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</span>
                    </div>
                    {r.comment && <p className="mt-2 text-sm leading-relaxed">{r.comment}</p>}
                    <p className="mt-2 text-xs text-muted-foreground">— {r.venue_name ?? "Venue"}</p>
                  </div>
                ))}
                {reviews.length > 6 && (
                  <p className="text-xs text-muted-foreground pt-2">+ {reviews.length - 6} more review{reviews.length - 6 === 1 ? "" : "s"}</p>
                )}
              </div>
            </div>
          </section>
        )}

        {photos.length === 0 && videos.length === 0 && certs.length === 0 && pvenues.length === 0 && (
          <p className="text-sm text-muted-foreground">This bartender hasn't filled out their portfolio yet.</p>
        )}
      </main>
    </div>
  );
}
